import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Card, StatusBadge, Ico, Icons, PlayerAvatar, resolveTeam, MatchDetailSkeleton } from '../../components/common';
import { matchAPI, commentAPI } from '../../services/api';
import { formatDateIST } from '../../utils/dateUtils';

export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdvanced } = useApp();
  const [text, setText] = useState('');
  const [comments, setComments] = useState([]);
  const [match, setMatch] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [ready, setReady] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [h2h, setH2h] = useState(null);
  const [h2hOpen, setH2hOpen] = useState(false);
  const [preds, setPreds] = useState({ total: 0, p1Count: 0, p2Count: 0, myPick: null });
  const [predicting, setPredicting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await matchAPI.getDetails(id);
        setMatch(res.data.match);
        setTournament(res.data.tournament);
        setPlayers(res.data.players);
        setComments(res.data.comments || []);
        if (res.data.predictions) setPreds(res.data.predictions);
        setReady(true);
      } catch (err) {
        console.error(err);
        setReady(true);
      }
    };
    load();
  }, [id]);

  // Fetch H2H when match loads (only for singles with both players)
  useEffect(() => {
    if (!match || !match.player1 || !match.player2) return;
    if (match.player1.includes('+') || match.player2.includes('+')) return; // skip doubles
    matchAPI.getHeadToHead(match.player1, match.player2)
      .then(res => { if (res.data?.total > 0) setH2h(res.data); })
      .catch(() => {});
  }, [match]);

  if (!ready) return <MatchDetailSkeleton />;
  if (!match) return <div className="container-sm"><h2>Match not found</h2></div>;

  const t1 = resolveTeam(match.player1, players);
  const t2 = resolveTeam(match.player2, players);
  const p1 = players.find(p => p.id === match.player1);
  const p2 = players.find(p => p.id === match.player2);
  const isLive = match.status === 'live';
  const isDone = match.status === 'completed';
  const sets = match.score?.sets || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await commentAPI.create(id, { text });
      if (res.data) {
        setComments(prev => [...prev, res.data]);
        setText('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '8px 12px 32px' }}>
      <div onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 16, color: 'var(--navy)', fontWeight: 600, fontSize: 14 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </div>

      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14 }}>
        <div className="match-hero-header" style={{ padding: '16px 18px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tournament?.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: "var(--font-display)" }}>{match.round}</span>
                {tournament?.surface && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--lime)', background: 'rgba(189,255,0,0.15)', padding: '2px 7px', borderRadius: 4, letterSpacing: 0.5 }}>{tournament.surface}</span>}
                {tournament?.matchType && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--lime)', background: 'rgba(189,255,0,0.15)', padding: '2px 7px', borderRadius: 4, letterSpacing: 0.5 }}>{tournament.matchType}</span>}
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              {isLive ? <span style={{ fontSize: 10, fontWeight: 800, color: '#FF5252', letterSpacing: 1, animation: 'pulse 1.5s infinite' }}>● LIVE</span> : <StatusBadge status={match.status} />}
            </div>
          </div>
        </div>

        <div style={{ padding: '18px 16px 14px' }}>
          {[{ team: t1, player: p1, pKey: 'p1', oKey: 'p2', pid: match.player1 }, { team: t2, player: p2, pKey: 'p2', oKey: 'p1', pid: match.player2 }].map(({ team, player, pKey, oKey, pid }, idx) => {
            const isWinner = isDone && match.winner === pid;
            const isLoser = isDone && match.winner && match.winner !== pid;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderBottom: idx === 0 ? '1px solid var(--gray-100)' : 'none', borderRadius: 8, ...(isWinner ? { background: 'rgba(67,160,71,0.05)' } : {}) }}>
                <Link to={pid && !pid.includes('+') ? `/players/${pid}` : '#'} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  <PlayerAvatar name={team.name} photo={player?.photo} size={36} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isWinner ? '#43A047' : isLoser ? 'var(--gray-300)' : 'var(--navy)', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "var(--font-display)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isWinner && <Ico d={Icons.trophy} size={12} color="#43A047" />}
                      {team.name || 'TBD'}
                    </div>
                    {player && <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>#{player.ranking} · {player.country}</div>}
                  </div>
                </Link>
                {sets.length > 0 && (
                  <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    {sets.map((s, i) => {
                      const won = s[pKey] > s[oKey];
                      const isCurrent = isLive && i === sets.length - 1;
                      return <span key={i} style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: won ? 700 : 400, fontFamily: "var(--font-display)", color: won ? 'var(--navy)' : 'var(--gray-300)', background: isCurrent ? 'rgba(229,57,53,0.08)' : won ? 'rgba(0,32,91,0.04)' : 'transparent', borderRadius: 5, ...(isCurrent ? { border: '1.5px solid #E53935' } : {}) }}>{s[pKey]}</span>;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '0 16px 14px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {match.date && <span className="chip-sm"><Ico d={Icons.calendar} size={10} color="#8892A6" />{formatDateIST(match.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
          {match.time && <span className="chip-sm"><Ico d={Icons.clock} size={10} color="#8892A6" />{match.time}</span>}
          {match.court && <span className="chip-sm">{match.court}</span>}
          {match.specialResult && <span className="chip-sm" style={{ color: '#E53935', borderColor: '#FFCDD2', background: '#FFEBEE' }}>{match.specialResult === 'walkover' ? 'W/O' : match.specialResult === 'retirement' ? 'RET' : 'DQ'}</span>}
        </div>
      </div>

      {/* Match Prediction Poll */}
      {match.player1 && match.player2 && !isDone && (() => {
        const total = preds.total;
        const p1Pct = total > 0 ? Math.round((preds.p1Count / total) * 100) : 50;
        const p2Pct = total > 0 ? Math.round((preds.p2Count / total) * 100) : 50;
        const myPick = preds.myPick;
        const p1Name = t1.name || 'Player 1';
        const p2Name = t2.name || 'Player 2';

        const handlePredict = async (pick) => {
          if (!currentUser || predicting) return;
          setPredicting(true);
          try {
            const res = await matchAPI.predict(id, pick);
            if (res.data) setPreds(res.data);
          } catch (e) { console.error(e); }
          setPredicting(false);
        };

        // After match completes, check if prediction was correct
        const correctPick = isDone && myPick && match.winner === myPick;

        return (
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', background: 'linear-gradient(135deg, rgba(126,87,194,0.06), rgba(30,136,229,0.04))' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #7E57C2, #AB47BC)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ico d={Icons.target} size={13} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <span className="heading" style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, display: 'block' }}>WHO WILL WIN?</span>
                  <span style={{ fontSize: 9, color: 'var(--gray-300)', fontWeight: 500 }}>{total} prediction{total !== 1 ? 's' : ''}</span>
                </div>
                {myPick && <span style={{ fontSize: 9, fontWeight: 700, color: '#7E57C2', background: 'rgba(126,87,194,0.1)', padding: '3px 8px', borderRadius: 4 }}>✓ Voted</span>}
              </div>
            </div>

            <div style={{ padding: '14px 16px' }}>
              {/* Prediction buttons */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[{ pid: match.player1, name: p1Name, photo: p1?.photo, count: preds.p1Count, pct: p1Pct, color: '#1E88E5', bg: 'rgba(30,136,229,0.08)' },
                  { pid: match.player2, name: p2Name, photo: p2?.photo, count: preds.p2Count, pct: p2Pct, color: '#E53935', bg: 'rgba(229,57,53,0.08)' }].map(({ pid, name, photo, count, pct, color, bg }, idx) => {
                  const isMyPick = myPick === pid;
                  return (
                    <button key={idx} onClick={() => handlePredict(pid)} disabled={!currentUser || predicting}
                      style={{ flex: 1, padding: '12px 10px', borderRadius: 10, border: isMyPick ? `2px solid ${color}` : '2px solid var(--gray-100)', background: isMyPick ? bg : 'var(--card-bg)', cursor: currentUser ? 'pointer' : 'default', transition: 'all 0.2s', opacity: predicting ? 0.6 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <PlayerAvatar name={name} photo={photo} size={32} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: isMyPick ? color : 'var(--navy)', fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{name}</span>
                      {total > 0 && (
                        <span style={{ fontSize: 18, fontWeight: 800, color: isMyPick ? color : 'var(--gray-400)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{pct}%</span>
                      )}
                      {isMyPick && <span style={{ fontSize: 8, fontWeight: 800, color, letterSpacing: 0.5 }}>YOUR PICK</span>}
                    </button>
                  );
                })}
              </div>

              {/* Progress bar */}
              {total > 0 && (
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--gray-100)' }}>
                  <div style={{ width: `${p1Pct}%`, background: 'linear-gradient(90deg, #1565C0, #1E88E5)', borderRadius: 3, transition: 'width 0.4s ease', minWidth: preds.p1Count > 0 ? 6 : 0 }} />
                  <div style={{ flex: 1, background: 'linear-gradient(90deg, #E53935, #C62828)', borderRadius: 3, minWidth: preds.p2Count > 0 ? 6 : 0 }} />
                </div>
              )}

              {/* Vote counts */}
              {total > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: '#1E88E5', fontWeight: 600 }}>{preds.p1Count} vote{preds.p1Count !== 1 ? 's' : ''}</span>
                  <span style={{ fontSize: 10, color: '#E53935', fontWeight: 600 }}>{preds.p2Count} vote{preds.p2Count !== 1 ? 's' : ''}</span>
                </div>
              )}

              {/* Sign in prompt */}
              {!currentUser && (
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Link to="/login" style={{ fontSize: 12, color: '#7E57C2', fontWeight: 600, textDecoration: 'none' }}>Sign in to predict</Link>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Prediction Result (show after match completes) */}
      {isDone && preds.total > 0 && (() => {
        const total = preds.total;
        const p1Pct = Math.round((preds.p1Count / total) * 100);
        const p2Pct = Math.round((preds.p2Count / total) * 100);
        const winnerPick = match.winner === match.player1 ? preds.p1Count : preds.p2Count;
        const winnerPct = Math.round((winnerPick / total) * 100);
        const myPick = preds.myPick;
        const gotItRight = myPick && myPick === match.winner;
        const p1Name = t1.name || 'Player 1';
        const p2Name = t2.name || 'Player 2';

        return (
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', background: 'linear-gradient(135deg, rgba(126,87,194,0.06), rgba(30,136,229,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #7E57C2, #AB47BC)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ico d={Icons.target} size={13} color="#fff" />
                </div>
                <span className="heading" style={{ fontSize: 10, letterSpacing: 1.5, margin: 0 }}>PREDICTION RESULTS</span>
              </div>
              <span style={{ fontSize: 10, color: 'var(--gray-300)', fontWeight: 600 }}>{total} vote{total !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: match.winner === match.player1 ? '#43A047' : 'var(--gray-300)', fontFamily: 'var(--font-display)' }}>{p1Name}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: match.winner === match.player2 ? '#43A047' : 'var(--gray-300)', fontFamily: 'var(--font-display)' }}>{p2Name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#1E88E5', fontFamily: 'var(--font-display)' }}>{p1Pct}%</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#E53935', fontFamily: 'var(--font-display)' }}>{p2Pct}%</span>
              </div>
              <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--gray-100)', marginBottom: 10 }}>
                <div style={{ width: `${p1Pct}%`, background: 'linear-gradient(90deg, #1565C0, #1E88E5)', borderRadius: 3, minWidth: preds.p1Count > 0 ? 6 : 0 }} />
                <div style={{ flex: 1, background: 'linear-gradient(90deg, #E53935, #C62828)', borderRadius: 3, minWidth: preds.p2Count > 0 ? 6 : 0 }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', textAlign: 'center' }}>
                {winnerPct}% predicted the winner correctly
              </div>
              {myPick && (
                <div style={{ marginTop: 8, textAlign: 'center', padding: '6px 12px', borderRadius: 8, background: gotItRight ? 'rgba(67,160,71,0.08)' : 'rgba(229,57,53,0.06)', border: `1px solid ${gotItRight ? 'rgba(67,160,71,0.2)' : 'rgba(229,57,53,0.15)'}` }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: gotItRight ? '#43A047' : '#E53935' }}>
                    {gotItRight ? '🎯 You predicted correctly!' : '❌ Your prediction didn\'t come through'}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Head-to-Head */}
      {h2h && h2h.total > 0 && (() => {
        const p1Name = t1.name || 'Player 1';
        const p2Name = t2.name || 'Player 2';
        const total = h2h.total;
        const p1W = h2h.p1Wins;
        const p2W = h2h.p2Wins;
        const p1Pct = total > 0 ? Math.round((p1W / total) * 100) : 0;
        return (
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14 }}>
            <button onClick={() => setH2hOpen(o => !o)} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'linear-gradient(135deg, rgba(0,32,91,0.04), rgba(30,136,229,0.04))', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #E53935, #FF7043)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ico d={Icons.trophy} size={13} color="#fff" />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <span className="heading" style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, display: 'block' }}>HEAD TO HEAD</span>
                  <span style={{ fontSize: 9, color: 'var(--gray-300)', fontWeight: 500 }}>{total} match{total !== 1 ? 'es' : ''}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: p1W >= p2W ? '#43A047' : 'var(--gray-300)', fontFamily: 'var(--font-display)' }}>{p1W}</span>
                <span style={{ fontSize: 10, color: 'var(--gray-300)', fontWeight: 600 }}>-</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: p2W >= p1W ? '#43A047' : 'var(--gray-300)', fontFamily: 'var(--font-display)' }}>{p2W}</span>
                <span style={{ fontSize: 14, color: 'var(--gray-300)', transform: h2hOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▾</span>
              </div>
            </button>

            {h2hOpen && (
              <div style={{ padding: '0 16px 14px' }}>
                {/* Bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 6px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', minWidth: 28, textAlign: 'right' }}>{p1Name.split(' ')[0]}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--gray-100)', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${p1Pct}%`, background: 'linear-gradient(90deg, #00205B, #1E88E5)', borderRadius: 4, transition: 'width 0.5s ease', minWidth: total > 0 && p1W > 0 ? 8 : 0 }} />
                    <div style={{ flex: 1, background: 'linear-gradient(90deg, #E53935, #FF7043)', borderRadius: 4, minWidth: total > 0 && p2W > 0 ? 8 : 0 }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', minWidth: 28 }}>{p2Name.split(' ')[0]}</span>
                </div>

                {/* Match history */}
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {h2h.history.map((m, i) => {
                    const winnerIsP1 = m.winner === h2h.player1Id;
                    const scoreStr = m.score?.sets?.map(s => `${s.p1}-${s.p2}`).join(', ') || '';
                    return (
                      <Link key={m.matchId || i} to={`/matches/${m.matchId}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.tournamentName} · {m.round}
                          </div>
                          <div style={{ fontSize: 9, color: 'var(--gray-300)', marginTop: 1 }}>
                            {m.date ? formatDateIST(m.date, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                            {m.specialResult ? ` · ${m.specialResult === 'walkover' ? 'W/O' : m.specialResult === 'retirement' ? 'RET' : 'DQ'}` : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {scoreStr && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', fontFamily: 'var(--font-display)' }}>{scoreStr}</span>}
                          <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: winnerIsP1 ? 'rgba(30,136,229,0.1)' : 'rgba(229,57,53,0.1)', color: winnerIsP1 ? '#1E88E5' : '#E53935' }}>
                            {winnerIsP1 ? p1Name.split(' ')[0] : p2Name.split(' ')[0]}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* AI Match Commentary */}
      {isDone && (match.aiCommentary || (currentUser?.role === 'organizer' && isAdvanced)) && (
        <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14, boxShadow: '0 2px 8px rgba(0,32,91,0.04)' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', background: 'linear-gradient(135deg, rgba(0,32,91,0.04), rgba(30,136,229,0.04))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #00205B, #1E88E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ico d={Icons.tennisBall} size={14} color="#BDFF00" />
                </div>
                <div>
                  <span className="heading" style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, display: 'block' }}>MATCH ANALYSIS</span>
                </div>
              </div>
              {currentUser?.role === 'organizer' && isAdvanced && (
                <button
                  onClick={async () => {
                    setRegenerating(true);
                    try {
                      const res = await matchAPI.regenerateCommentary(id);
                      if (res.data) setMatch(res.data);
                    } catch (e) { console.error(e); }
                    setRegenerating(false);
                  }}
                  disabled={regenerating}
                  style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, background: regenerating ? 'var(--gray-200)' : '#00205B', color: regenerating ? 'var(--gray-400)' : '#BDFF00', border: 'none', borderRadius: 6, cursor: regenerating ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)', letterSpacing: 0.5 }}
                >
                  {regenerating ? '⏳ Generating...' : match.aiCommentary ? '↻ Regenerate' : '✦ Generate'}
                </button>
              )}
            </div>
          </div>
          <div style={{ padding: '14px 16px' }}>
            {match.aiCommentary ? (
              <div style={{ fontSize: 13, color: '#3D4759', lineHeight: 1.75 }}>
                {(() => {
                  const renderText = (text) => {
                    const parts = text.split(/\*\*(.*?)\*\*/g);
                    return parts.map((part, j) => j % 2 === 1
                      ? <strong key={j} style={{ color: '#00205B' }}>{part}</strong>
                      : <span key={j}>{part}</span>
                    );
                  };
                  const iconMap = { '🎾': { icon: Icons.tennisBall, color: '#43A047' }, '📊': { icon: Icons.clipboard, color: '#1E88E5' }, '🎯': { icon: Icons.target, color: '#E53935' }, '⭐': { icon: Icons.medal, color: '#FB8C00' } };
                  const sections = [];
                  let current = null;
                  match.aiCommentary.split('\n').forEach(line => {
                    const trimmed = line.trim();
                    if (!trimmed) return;
                    const headerMatch = trimmed.match(/^([\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B50}])\s*(.+)/u);
                    if (headerMatch && /[A-Z]{2,}/.test(headerMatch[2])) {
                      current = { emoji: headerMatch[1], title: headerMatch[2].replace(/^\**\s*/, '').replace(/\**\s*:?$/, '').trim(), lines: [] };
                      sections.push(current);
                    } else if (current) {
                      current.lines.push(trimmed);
                    } else {
                      sections.push({ emoji: null, title: null, lines: [trimmed] });
                    }
                  });
                  if (sections.length <= 1 && !sections[0]?.emoji) {
                    return <p style={{ margin: 0 }}>{renderText(match.aiCommentary)}</p>;
                  }
                  return sections.map((sec, i) => {
                    if (!sec.emoji) return sec.lines.map((l, j) => <p key={`${i}-${j}`} style={{ margin: '6px 0' }}>{renderText(l)}</p>);
                    const mapped = iconMap[sec.emoji] || { icon: Icons.target, color: '#5F6B80' };
                    return (
                      <div key={i} style={{ marginBottom: i < sections.length - 1 ? 16 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <div style={{ width: 22, height: 22, borderRadius: 6, background: `${mapped.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Ico d={mapped.icon} size={12} color={mapped.color} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 800, color: '#00205B', letterSpacing: 1, fontFamily: 'var(--font-display)' }}>{sec.title}</span>
                        </div>
                        {sec.lines.map((l, j) => {
                          if (l.startsWith('- ') || l.startsWith('• ')) return (
                            <div key={j} style={{ display: 'flex', gap: 8, paddingLeft: 30, marginTop: 4 }}>
                              <span style={{ color: mapped.color, fontWeight: 700 }}>•</span>
                              <span>{renderText(l.slice(2))}</span>
                            </div>
                          );
                          return <p key={j} style={{ margin: '2px 0', paddingLeft: 30 }}>{renderText(l)}</p>;
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            ) : currentUser?.role === 'organizer' && isAdvanced ? (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--gray-300)' }}>
                  {regenerating ? 'Generating analysis...' : 'Click "Generate" to create an AI analysis for this match.'}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div style={{ marginTop: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span className="heading" style={{ fontSize: 11, letterSpacing: 2 }}>COMMENTS</span>
          <span style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 600 }}>{comments.length}</span>
        </div>

        {currentUser ? (
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input className="form-input" style={{ flex: 1, padding: '10px 12px', fontSize: 13 }} placeholder="Write a comment..." value={text} onChange={e => setText(e.target.value)} />
            <button type="submit" disabled={!text.trim()} style={{ padding: '10px 18px', background: text.trim() ? '#00205B' : 'var(--gray-200)', color: text.trim() ? '#BDFF00' : 'var(--gray-400)', border: 'none', borderRadius: 8, fontWeight: 700, cursor: text.trim() ? 'pointer' : 'default', fontSize: 12, fontFamily: "var(--font-display)", flexShrink: 0, transition: 'all 0.2s' }}>Post</button>
          </form>
        ) : (
          <div style={{ borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--card-bg)', padding: '14px', textAlign: 'center', marginBottom: 12 }}>
            <Link to="/login" style={{ fontSize: 13, color: '#1E88E5', fontWeight: 600, textDecoration: 'none' }}>Sign in to comment</Link>
          </div>
        )}

        {comments.length === 0 ? (
          <div style={{ borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--card-bg)', padding: '24px 16px', textAlign: 'center' }}>
            <p style={{ color: 'var(--gray-400)', fontSize: 13, margin: 0 }}>No comments yet. Be the first!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...comments].reverse().map(c => (
              <div key={c.id || c._id} style={{ borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--card-bg)', padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PlayerAvatar name={c.userName} size={22} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{c.userName}</span>
                  </div>
                  <span style={{ fontSize: 9, color: 'var(--gray-300)' }}>{new Date(c.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.5, paddingLeft: 28 }}>{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
